// ===== Entrada do sistema: login por codigo de acesso (e magic link antigo) =====
// Quem manda e o Supabase Auth: esta tela pede o acesso por e-mail e mostra o campo do CODIGO que chega
// (spec 2026-10-27-login-por-codigo-no-celular) -- a sessao volta assinada pelo servidor (src/data/supabase.js)
// e e lida por App() via onAuthStateChange/getSession, nunca escrita aqui.
// O link do e-mail antigo continua valendo: ver ConfirmarAcesso, no fim do arquivo.
import React from 'react';
import { limparAtividade } from './inatividade.js';
import { enderecoSemOLink } from './link-de-acesso.js';
import { limparCodigo, codigoValido } from './codigo-de-acesso.js';
import { Icon } from './icons.jsx';
import {
  entrarComMagicLink, sessaoAtual, sair, sairDasOutrasSessoes, DOMINIO_PERMITIDO as DOMINIO_BASE,
  erroDoLinkDeAcesso, mensagemDoErroDeAcesso, mensagemDoErroDeEnvio, confirmarLinkDeAcesso, mensagemDoErroDoLink,
  confirmarCodigoDeAcesso, mensagemDoErroDoCodigo,
} from './data/supabase.js';
import { consumirMotivoLogout, limiteDoAparelho, rotuloDoLimite } from './inatividade.js';

// Mantido com o "@" para uso direto na interface (placeholder, mensagens de erro).
// O valor "cru" (sem @) é o exportado por data/supabase.js e é o que também
// alimenta a trigger do banco — ver supabase/migrations/20260810120024_bloqueia_dominio_externo.sql.
const DOMINIO_PERMITIDO = `@${DOMINIO_BASE}`;

// lerSessao/encerrarSessao mantêm os mesmos nomes de antes para não quebrar
// quem os importa — só a implementação deixou de ser localStorage.
// gravarSessao não existe mais: quem grava a sessão é o Supabase (cookie/
// storage do próprio SDK), não o front.
const lerSessao = sessaoAtual;
const encerrarOutrasSessoes = sairDasOutrasSessoes;

// Sair APAGA a marca de inatividade, e isso passou a importar em 15/09.
//
// `limparAtividade` existia exportada e nunca era chamada por ninguem. A marca
// ficava no localStorage para sempre -- entre sessoes, entre dias --, e quem
// voltasse mais de 2h depois entrava pelo magic link e era expulso no ato, com
// cara de "o magic link parou de funcionar". A marca agora tem dono (ver
// src/inatividade.js), o que ja resolve; apagar no logout e higiene: relogio de
// sessao encerrada nao tem por que continuar guardado.
//
// Aqui, e nao dentro de `sair`: sao tres botoes de Sair (shell, perfil e as
// telas de espera do app) e todos passam por este nome.
const encerrarSessao = async (...args) => {
  limparAtividade();
  return sair(...args);
};

function LoginScreen({ avisoInicial = null }) {
  const [etapa, setEtapa] = React.useState("email");
  const [email, setEmail] = React.useState("");
  const [erro, setErro] = React.useState(null);
  const [enviando, setEnviando] = React.useState(false);
  // Etapa "enviado": o codigo que a pessoa digita e o estado da confirmacao. Sucesso NAO reseta `confirmando`: a sessao chega ao App por
  // onAuthStateChange e esta tela sai de cena, como em ConfirmarAcesso.
  const [codigo, setCodigo] = React.useState("");
  const [erroCodigo, setErroCodigo] = React.useState(null);
  const [confirmando, setConfirmando] = React.useState(false);
  // Motivo de o usuário ter voltado do link sem entrar (expirado, já usado).
  // Some assim que ele pede um link novo — a essa altura já cumpriu o papel.
  const [avisoDoLink, setAvisoDoLink] = React.useState(() => avisoInicial || mensagemDoErroDeAcesso(erroDoLinkDeAcesso));
  // Consumido uma vez: se o ultimo logout foi por inatividade (2h), explica.
  // Some quando o usuario pede um link novo, como o aviso do link.
  const [avisoInatividade, setAvisoInatividade] = React.useState(() => consumirMotivoLogout() === 'inatividade');
  // Segundos que faltam para poder pedir outro link.
  //
  // POR QUE ISTO EXISTE (levantamento de 15/09). O Supabase so aceita um pedido
  // a cada 60 segundos -- "a user can only request an OTP once every 60 seconds"
  // --, e, o que e pior, CADA PEDIDO NOVO INVALIDA O ANTERIOR. Reproduzido:
  // gerar um token novo faz o antigo devolver
  // {"error_code":"otp_expired","msg":"Token has expired or is invalid"}.
  //
  // Ate hoje a tela nao sabia disso. A unica saida oferecida depois do envio era
  // "Usar outro e-mail", e quem nao via o e-mail chegar clicava ali, digitava o
  // MESMO endereco e mandava de novo -- matando o proprio link. Quando o primeiro
  // e-mail chegava, clicar dava "expirado". A tela deixava a pessoa quebrar o
  // acesso e depois culpava o link.
  const [espera, setEspera] = React.useState(0);
  const ref = React.useRef(null);

  React.useEffect(() => { ref.current && ref.current.focus(); }, [etapa]);

  // Um intervalo so, vivo enquanto ha espera. Sem o `clearInterval` a tela
  // continuaria contando depois de desmontada.
  React.useEffect(() => {
    if (espera <= 0) return;
    const t = setInterval(() => setEspera((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [espera > 0]);

  const enviar = async (e) => {
    e && e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return setErro("Digite o seu e-mail corporativo.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return setErro("Este e-mail não parece válido.");
    if (!v.endsWith(DOMINIO_PERMITIDO)) return setErro(`O acesso é restrito a endereços ${DOMINIO_PERMITIDO}.`);
    setErro(null);
    setAvisoDoLink(null);   // pediu link novo: o aviso do link velho já cumpriu o papel
    setAvisoInatividade(false);
    setEnviando(true);
    try {
      const { enviadoPara } = await entrarComMagicLink(v);
      setEmail(enviadoPara);
      setEtapa("enviado");
      setCodigo("");   // o codigo do e-mail antigo morreu ao pedir outro
      setErroCodigo(null);
      setEspera(60);   // o limite e do proprio Supabase; a tela so passa a conhece-lo
    } catch (err) {
      // O servidor fala ingles ("For security purposes, you can only request
      // this after 14 seconds") e o vendedor nao tem por que ler isso. Quando ele
      // diz QUANTOS segundos faltam, o contador passa a usar esse numero -- assim
      // a tela mostra o que o servidor vai realmente aceitar, e nao o nosso
      // palpite de 60.
      const { texto, segundos } = mensagemDoErroDeEnvio(err);
      setErro(texto);
      if (segundos > 0) setEspera(segundos);
    } finally {
      setEnviando(false);
    }
  };

  const confirmar = async (e) => {
    e && e.preventDefault();
    if (confirmando || enviando) return;
    const limpo = limparCodigo(codigo);
    // Vazio ou com letra nem sai do aparelho: cada tentativa gasta uma das 30 verificacoes por 5 min do Auth.
    if (!codigoValido(limpo)) return setErroCodigo("Digite o código que chegou no e-mail, só números.");
    setErroCodigo(null);
    setConfirmando(true);
    try {
      await confirmarCodigoDeAcesso(email, limpo);   // a sessao chega ao App por onAuthStateChange
    } catch (err) {
      console.error('Falha ao confirmar o codigo de acesso:', err?.status, err?.code, err?.message);
      setErroCodigo(mensagemDoErroDoCodigo(err));
      setConfirmando(false);
    }
  };

  return (
    <div className="login" data-screen-label="Login">
      <div className="login-painel">
        <div className="login-marca">
          <div className="lg-logo">P</div>
          <div>
            <div className="lg-nome">BeamCRM</div>
            <div className="lg-org">PDLED Iluminação</div>
          </div>
        </div>

        {etapa === "email" ? (
          <form className="login-card" onSubmit={enviar}>
            <h1>Entrar</h1>
            {avisoInatividade && !avisoDoLink && (
              <div className="lg-aviso" style={{ marginBottom: 14 }} role="status">
                <Icon name="clock" size={12} /> Sua sessão foi encerrada após {rotuloDoLimite(limiteDoAparelho())} sem atividade. Entre novamente para continuar.
              </div>
            )}
            {avisoDoLink && (
              <div className="lg-erro" style={{ marginBottom: 14 }} role="alert">
                <Icon name="alert-triangle" size={12} /> {avisoDoLink}
              </div>
            )}
            <p className="lg-sub">Enviamos um código de acesso para o seu e-mail. Sem senha para lembrar, sem senha para vazar.</p>
            <label className="lg-label" htmlFor="lg-email">E-mail corporativo</label>
            <div className={"lg-input" + (erro ? " erro" : "")}>
              <Icon name="mail" size={15} />
              <input
                id="lg-email" ref={ref} type="email" autoComplete="email" spellCheck="false"
                placeholder={"nome" + DOMINIO_PERMITIDO}
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (erro) setErro(null); }} />
            </div>
            {erro ? <div className="lg-erro"><Icon name="alert-triangle" size={12} /> {erro}</div>
                  : <div className="lg-hint">Somente endereços {DOMINIO_PERMITIDO}.</div>}
            {/* A espera vale aqui tambem. Zera-la ao voltar por "usar outro
                e-mail" foi o que fez o Kaio levar o erro cru do servidor em
                15/09: a tela liberava o envio que o servidor ia recusar. */}
            <button className="btn primary lg-btn" type="submit" disabled={enviando || espera > 0}>
              {enviando ? "Enviando…"
                : espera > 0 ? `Aguarde ${espera}s`
                : <><Icon name="send" size={14} /> Enviar código de acesso</>}
            </button>
          </form>
        ) : (
          <form className="login-card" onSubmit={confirmar} noValidate>
            {/* noValidate: `pattern="[0-9]*"` faria o navegador barrar o envio de "123 456" ou "123-456" com a dica nativa do navegador, ANTES de limparCodigo
                rodar. Quem valida e confirmar(), com mensagem em portugues; o `pattern` fica como dica de teclado numerico (NAO provado em aparelho). */}
            <div className="lg-ic"><Icon name="mail" size={22} /></div>
            <h1>Verifique seu e-mail</h1>
            {/* O endereco em LINHA PROPRIA: junto do texto corrido ele colava na frase seguinte e o olho nao achava onde o e-mail termina, que e
                o dado que a pessoa veio conferir. `overflowWrap: anywhere` porque endereco longo nao tem espaco para quebrar. */}
            <p className="lg-sub" style={{ marginBottom: 2 }}>Enviamos um código de acesso para</p>
            <p style={{ margin: "0 0 10px", fontWeight: 700, overflowWrap: "anywhere" }}>{email}</p>
            <p className="lg-sub">Digite abaixo o código do e-mail. Ele vale por 1 hora e só pode ser usado uma vez.</p>
            <label className="lg-label" htmlFor="lg-codigo">Código de acesso</label>
            {/* Sem `maxLength`: a documentacao diz 6 digitos e a medicao de 15/09 achou 8 (spec, decisao 2). `autoComplete="one-time-code"` deixa o
                teclado do celular sugerir o codigo quando o app de e-mail permitir -- NAO provado em aparelho. */}
            <div className={"lg-input" + (erroCodigo ? " erro" : "")}>
              <Icon name="key" size={15} />
              <input
                id="lg-codigo" className="lg-codigo" ref={ref} type="text" inputMode="numeric" pattern="[0-9]*"
                autoComplete="one-time-code" spellCheck="false" autoCorrect="off"
                placeholder="Código do e-mail"
                value={codigo}
                onChange={(e) => { setCodigo(e.target.value); if (erroCodigo) setErroCodigo(null); }} />
            </div>
            {erroCodigo
              ? <div className="lg-erro" role="alert"><Icon name="alert-triangle" size={12} /> {erroCodigo}</div>
              : <div className="lg-hint">Pode colar o código inteiro.</div>}
            <button className="btn primary lg-btn" type="submit" disabled={confirmando || enviando}>
              {confirmando ? "Entrando…" : <><Icon name="send" size={14} /> Entrar</>}
            </button>
            {/* O texto no <span>: `.lg-aviso` e `display:flex`, entao cada no solto virava uma coluna. */}
            <div className="lg-aviso" style={{ margin: "14px 0", alignItems: "flex-start", textAlign: "left" }} role="status">
              <Icon name="alert-triangle" size={12} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>Pedir outro código <strong>cancela este</strong>. Use sempre o e-mail mais recente.</span>
            </div>
            {erro && (
              <div className="lg-erro" style={{ marginBottom: 10 }} role="alert">
                <Icon name="alert-triangle" size={12} /> {erro}
              </div>
            )}
            {/* type="button": dentro de um <form>, botao sem tipo e "submit" e confirmaria o codigo. */}
            <button
              className="btn lg-btn" type="button"
              onClick={enviar}
              disabled={enviando || confirmando || espera > 0}
              title={espera > 0 ? "O Supabase aceita um pedido por minuto" : "Enviar outro código para o mesmo e-mail"}>
              {enviando ? "Enviando…"
                : espera > 0 ? `Reenviar código em ${espera}s`
                : <><Icon name="send" size={14} /> Reenviar código</>}
            </button>
            <button className="lg-voltar" type="button" onClick={() => setEtapa("email")}>Usar outro e-mail</button>
          </form>
        )}
      </div>
      <div className="login-rodape">Acesso restrito à equipe PDLED · sessões são registradas</div>
    </div>
  );
}

// Tela de confirmacao do link que veio pelo NOSSO dominio. Existe para que o token so seja gasto no CLIQUE: provedores de e-mail e filtros de
// spam abrem os links das mensagens antes da pessoa (documentacao do Supabase, auth-email-templates), e um link que se consome ao carregar a
// pagina chegaria "expirado" a quem clicasse depois. Aqui o carregamento nao gasta nada.
function ConfirmarAcesso({ link, onFalha }) {
  const [entrando, setEntrando] = React.useState(false);
  const entrar = async () => {
    if (entrando) return;
    setEntrando(true);
    // o token e de uso unico e nao pode ficar no historico nem no endereco copiado: sai antes de qualquer espera
    window.history.replaceState(null, '', enderecoSemOLink(window.location.href));
    try {
      await confirmarLinkDeAcesso(link);   // a sessao chega ao App por onAuthStateChange
    } catch (e) {
      console.error('Falha ao confirmar o link de acesso:', e?.message);
      setEntrando(false);
      onFalha(mensagemDoErroDoLink(e));
    }
  };
  return (
    <div className="login" data-screen-label="Confirmar acesso">
      <div className="login-painel">
        <div className="login-marca">
          <div className="lg-logo">P</div>
          <div>
            <div className="lg-nome">BeamCRM</div>
            <div className="lg-org">PDLED Iluminação</div>
          </div>
        </div>
        <div className="login-card">
          <h1>Entrar no BeamCRM</h1>
          <p className="lg-sub">Seu link de acesso foi recebido. Clique para concluir a entrada neste navegador.</p>
          <button className="btn primary lg-btn" type="button" onClick={entrar} disabled={entrando} data-testid="confirmar-acesso">
            {entrando ? "Entrando…" : <><Icon name="send" size={14} /> Entrar</>}
          </button>
        </div>
      </div>
      <div className="login-rodape">Acesso restrito à equipe PDLED · sessões são registradas</div>
    </div>
  );
}

export { ConfirmarAcesso, LoginScreen, lerSessao, encerrarSessao, encerrarOutrasSessoes, DOMINIO_PERMITIDO };
